import { motion } from 'framer-motion';
import { Calendar } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next'; // ADD THIS IMPORT
import { Service } from '../../../shared/hooks/useServiceSelection';
import FooterNavigation from '../../../shared/components/Navigation';
import { useDateTimeData } from '../../../shared/hooks/useDateTimeData';
import DatePickerComponent from './DatePickerComponent';
import TimeSlotsComponent from './TimeSlotsComponent';

interface DateSelectionProps {
  selectedServices: Service[];
  selectedTimeZone: string;
  selectedLocation: any;
  // Add the passed props from parent
  selectedDate: Date | null;
  setSelectedDate: (date: Date | null) => void;
  selectedTime: string | null;
  setSelectedTime: (time: string) => void;
  onNext: () => void;
  onPrev: () => void;
  nodeData?: any;
}

const DateSelection: React.FC<DateSelectionProps> = ({
  selectedServices,
  selectedTimeZone,
  selectedLocation,
  // Receive props from parent
  selectedDate: propSelectedDate,
  setSelectedDate: propSetSelectedDate,
  selectedTime: propSelectedTime,
  setSelectedTime: propSetSelectedTime,
  onNext,
  onPrev,
  nodeData
}) => {
  const { t } = useTranslation(); // ADD THIS LINE

  // Use our shared hook for date/time data and logic, but not for state management
  const {
    fetching,
    error,
    timeGroups,
    hasAvailableSlots,
    setSelectedDate
  } = useDateTimeData({
    selectedServices,
    selectedTimeZone,
    selectedLocation,
    initialDate: propSelectedDate,
  });

  // Get colors from nodeData
const colors = nodeData?.settings?.colors || {
  cardBackground: '#000000F2',
  cardBorder: '#DDB88919',
  titleText: '#DDB889',
  subtitleText: '#DDB889B3',
  iconColor: '#DDB889B3',
  dateBackground: '#000000B3',
  dateBorder: '#DDB88919',
  dateText: '#DDB889',
  selectedDateBackground: '#DDB889',
  selectedDateBorder: '#DDB889',
  selectedDateText: '#000000',
  timeslotBackground: '#000000B3',
  timeslotBorder: '#DDB88919',
  timeslotText: '#DDB889',
  selectedTimeslotBackground: 'rgba(0,0,0,0.95)',  // ✨ Same as unselected!
  selectedTimeslotBorder: '#22c55e49',
  selectedTimeslotText: '#22c55e',  // ✨ Green text!
  selectedTimeslotShadow: 'rgba(34,197,94,0.3)',
  timeGroupText: '#DDB889',
  summaryBackground: '#000000B3',
  summaryBorder: '#DDB88919',
  summaryText: '#DDB889',
  buttonBackground: '#DDB889',
  buttonText: '#000000'
};


  // For DatePickerComponent
  const portalId = `lp-date-picker-portal-${Math.random().toString(36).substr(2, 9)}`;
  const container = document.getElementById(portalId);
  const isMobile = window.innerWidth < 768;

  // Handle date selection using the parent's setter
  const handleDateChange = (date: Date | null) => {
    propSetSelectedDate(date);
    setSelectedDate(date)
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="lp-w-full lp-backdrop-blur-sm lp-p-6 lp-rounded-2xl lp-transition-all lp-duration-700"
      style={{
        backgroundColor: colors.cardBackground,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: colors.cardBorder,
        color: colors.titleText,
        boxShadow: '0 0 15px rgba(221,184,137,0.07)'
      }}
    >
      {/* Header */}
      <div className="lp-flex lp-items-center lp-justify-center lp-mb-6">
        <div className="lp-flex lp-items-center lp-space-x-4">
          <div className="lp-h-[1px] lp-w-16 lp-bg-gradient-to-r lp-from-transparent lp-opacity-20"
            style={{ backgroundImage: `linear-gradient(to right, transparent, ${colors.titleText})` }}></div>
          <h3 className="lp-text-2xl lp-font-medium lp-text-center lp-flex lp-items-center"
            style={{ color: colors.titleText }}>
            <Calendar className="lp-mr-2 lp-hidden md:lp-block" size={24} color={colors.iconColor} />
            {t('booking.selectDateTime')} {/* CHANGED: "Select Date and Time" */}
          </h3>
          <div className="lp-h-[1px] lp-w-16 lp-bg-gradient-to-l lp-from-transparent lp-opacity-20"
            style={{ backgroundImage: `linear-gradient(to left, transparent, ${colors.titleText})` }}></div>
        </div>
      </div>

      {/* Date Picker */}
      <DatePickerComponent
        selectedDate={propSelectedDate}
        handleDateChange={handleDateChange}
        isMobile={isMobile}
        portalId={portalId}
        container={container}
        colors={colors}
      />

      {/* Time Slots */}
      <div className="lp-mb-6 lp-min-h-[200px]">
        <TimeSlotsComponent
          fetching={fetching}
          error={error}
          timeGroups={timeGroups}
          hasAvailableSlots={hasAvailableSlots}
          selectedTime={propSelectedTime}
          //@ts-expect-error
          setSelectedTime={propSetSelectedTime}
          colors={colors}
        />
      </div>

      {/* Footer Navigation */}
      <FooterNavigation
        onPrev={onPrev}
        onNext={onNext}
        disabled={!propSelectedDate || !propSelectedTime}
      />
    </motion.div>
  );
};

export default DateSelection;
